import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Icon } from "@lynx/ui-components";
import { Spinner } from "react-bootstrap";

import { payoutThunks } from "actions-thunks/payout-thunks";
import { accountThunks } from "actions-thunks/account-thunks";
import { PAYMENT_METHOD_KINDS } from "api/payout-api";
import { PAYMENT_METHODS_ERROR, TO_SUPPORT_PAGE, TO_WIRE_MANAGEMENT } from "reducers/types";
import useStatistic from "ui/hooks/useStatistic";
import BankAccountCard from "../components/BankAccountCard";
import PayoutNotice from "../components/PayoutNotice";
import { getOpenPayouts } from "../wireUtils";
import BankAccountForm from "./BankAccountForm";

import "../payout-modals.scss";
import "./bank-account-details.scss";

const KIND = PAYMENT_METHOD_KINDS.bankAccount;
const WIRES_LIST_PARAMS = { page: 1, pageLen: 25, sort: "-createdAt" };

const BankAccountDetailsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentAccount, paymentMethods, countries, wiresList } = useSelector(
    (state) => state.accounts
  );
  const { loading } = useSelector((state) => state.utils);
  const { errorMessage } = useSelector((state) => state.errors);
  const formRef = useRef(null);
  const [formState, setFormState] = useState({ dirty: false, valid: true });
  const [editing, setEditing] = useState(false);

  const serverError = errorMessage?.find((i) => i.type === PAYMENT_METHODS_ERROR)?.message || "";
  const method = paymentMethods?.[KIND] || null;
  const hasOpenPayouts = getOpenPayouts(wiresList).length > 0;
  const isFirstSetup = !method;
  const mode = isFirstSetup || editing ? "edit" : "view";

  useStatistic(currentAccount?._id, "Open page Bank account details");

  useEffect(() => {
    if (currentAccount?._id) {
      dispatch(payoutThunks.getPaymentMethodsTC(currentAccount._id));
      // open pay-outs block bank-detail changes
      if (wiresList === null) {
        dispatch(payoutThunks.loadWiresWithPayoutsTC(currentAccount._id, WIRES_LIST_PARAMS));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount, dispatch]);

  useEffect(() => {
    if (!countries) {
      dispatch(accountThunks.setEntityTC("country"));
    }
  }, [countries, dispatch]);

  const handleSubmit = async (data) => {
    try {
      await dispatch(payoutThunks.savePaymentMethodTC(currentAccount._id, KIND, data, method?._id));
      setEditing(false);
    } catch (e) {
      // the server message (e.g. open pay-outs) is shown under the form
    }
  };

  const handleDiscard = () => {
    formRef.current?.reset();
    if (!isFirstSetup) setEditing(false);
  };
  const handleSave = () => formRef.current?.submit();
  const handleStateChange = useCallback((state) => setFormState(state), []);

  const personal = currentAccount?.personal || {};
  const profileName = [personal.firstName, personal.lastName].filter(Boolean).join(" ");
  const traderId = currentAccount?.reference?.client || "";

  const contactSupport = (
    <button type="button" className="link-button" onClick={() => navigate(TO_SUPPORT_PAGE)}>
      Contact Support
    </button>
  );

  return (
    <div className="bank-account-details-wrapper">
      {loading && <Spinner className="spinner" animation="grow" />}

      <div className="bank-account-details-header">
        <div className="bank-account-details-title">
          <button
            type="button"
            className="bank-account-details-back"
            id="bank-account-details-back"
            onClick={() => navigate(TO_WIRE_MANAGEMENT)}
            aria-label="Back to Wire Management"
          >
            <Icon name="chevron_left" />
          </button>
          <h2>{isFirstSetup ? "Add bank account details" : "Bank account details"}</h2>
        </div>
        {mode === "edit" && (
          <div className="bank-account-details-actions">
            <button
              type="button"
              className="text-button discard-button"
              id="bank-account-discard"
              onClick={handleDiscard}
              disabled={loading || (isFirstSetup && !formState.dirty)}
            >
              Discard
            </button>
            <button
              type="button"
              className="text-button save-button"
              id="bank-account-save"
              onClick={handleSave}
              disabled={loading || !formState.dirty || !formState.valid || hasOpenPayouts}
            >
              {isFirstSetup ? "Save bank details" : "Save changes"}
            </button>
          </div>
        )}
      </div>

      {mode === "view" ? (
        <div className="bank-account-details-view">
          <p className="bank-account-details-intro">Your current bank details have been saved.</p>
          <BankAccountCard bank={method} className="bank-account-card-plain" />
          <p className="bank-account-details-meta">
            {method.beneficiaryType || "Individual"} · {method.beneficiaryName || profileName}
            <br />
            Trader / Group ID: {traderId}
            <br />
            Beneficiary email: {method.beneficiaryEmail || personal.email}
          </p>
          {hasOpenPayouts ? (
            <PayoutNotice type="warning" title="Bank details cannot be changed">
              Wait until your active payout request is resolved or {contactSupport}
            </PayoutNotice>
          ) : (
            <>
              <p className="bank-account-details-intro">
                You can change your bank details only when you have no active payout requests. If you
                need an active request cancelled, please contact Support. Historical payouts keep their
                saved bank details.
              </p>
              <button
                type="button"
                className="edit-bank-details-button"
                id="bank-account-edit"
                onClick={() => setEditing(true)}
              >
                Edit bank details
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="bank-account-details-form">
          <p className="bank-account-details-intro">
            {isFirstSetup
              ? "Add your bank account to receive payouts. You can have one current bank account. Bank details can be saved only when you have no active payout requests."
              : "No active payout requests. You can update your current bank details. Historical payouts keep their saved bank details."}
          </p>
          {hasOpenPayouts && (
            <PayoutNotice type="warning" title="Bank details cannot be changed">
              Wait until your active payout request is resolved or {contactSupport}
            </PayoutNotice>
          )}
          <BankAccountForm
            ref={formRef}
            method={method}
            countries={countries}
            account={currentAccount}
            onSubmit={handleSubmit}
            onStateChange={handleStateChange}
          />
          {serverError && <PayoutNotice type="error">{serverError}</PayoutNotice>}
        </div>
      )}
    </div>
  );
};

export default BankAccountDetailsPage;
